import { type RawImageField } from "@src/app/types/Opensearch";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import Date from "../Elements/Typography/Date";
import Heading from "../Elements/Typography/Heading";
import Image from "../ResponsiveImage/Image";

export type ArticleCardElementProps = {
  hasParagraph?: boolean;
  hasDate?: boolean;
};

export type ArticleCardStyleProps = {
  cardWrapper?: string;
  contentWrapper?: string;
  paragraphStyle?: string;
  titleWrapper?: string;
  titleStyle?: string;
  imageWrapperStyle?: string;
  imageStyle?: string;
};

export type ArticleCardProps = {
  linkTo: string;
  title: string;
  paragraph?: string;
  fullDate?: string;
  time?: string;
  displayWidth: number;
  displayHeight: number;
  imageField?: RawImageField;
  imageAlt?: string;
  urlPath?: string;
  overrideStyle?: ArticleCardStyleProps;
  elements?: ArticleCardElementProps;
};

export default function ArticleCard({
  title,
  paragraph,
  fullDate,
  time,
  displayWidth,
  displayHeight,
  imageField,
  imageAlt,
  linkTo,
  overrideStyle,
  elements,
}: ArticleCardProps) {
  const {
    cardWrapper,
    contentWrapper,
    imageWrapperStyle,
    imageStyle,
    paragraphStyle,
    titleStyle,
    titleWrapper,
  } = overrideStyle || {};
  const { hasParagraph = true, hasDate } = elements || {};

  return (
    <div
      className={twMerge(
        "flex w-full gap-sm py-5 border-b border-pink-300",
        cardWrapper,
      )}
    >
      <Link
        reloadDocument
        to={linkTo}
        className={twMerge(
          "w-full flex-shrink-0 md:flex-grow-0 sm:w-[260px]",
          imageWrapperStyle,
        )}
      >
        <Image
          displayWidth={displayWidth}
          srcWidth={displayWidth}
          srcHeight={displayHeight}
          src={imageField?.url ?? "https://dummyimage.com/600x400/000/fff"}
          alt={imageAlt ?? title}
          className={twMerge("w-full", imageStyle)}
        />
      </Link>

      <div className={twMerge("flex flex-col w-full gap-xs", contentWrapper)}>
        <div className={twMerge("flex flex-col gap-sm", titleWrapper)}>
          <Link reloadDocument to={linkTo}>
            <Heading
              Element="h2"
              className={twMerge(
                "font-bold font-primary hover:underline hover:text-red-200 text-red-100",
                titleStyle,
              )}
            >
              {title}
            </Heading>
          </Link>
        </div>

        {hasParagraph && (
          <p
            className={twMerge(
              "font-regular font-primary text-base lg:text-xs leading-300",
              paragraphStyle,
            )}
          >
            {paragraph}
          </p>
        )}

        {hasDate && (
          <Date>
            {fullDate}
            {time ? <span className="lowercase"> - {time}</span> : ""}
          </Date>
        )}
      </div>
    </div>
  );
}
